import React, { useEffect, useState } from "react";

import classNames from "classnames";
import Link from "next/link";

import { ProductData } from "data/product/products";

import { CreatorInfo } from "~/components/common/CreatorInfo";
import { DotSeparatorIcon } from "~/components/common/Icons/DotSeparatorIcon";
import { PlayVideoIcon } from "~/components/common/Icons/PlayVideoIcon";
import { StarReviewsIcon } from "~/components/common/Icons/StarReviewsIcon";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { VideoPlayer } from "~/components/videoProductPage/common/VideoPlayer/VideoPlayer";
import { VideoProgressBar } from "~/components/videoProductPage/common/VideoProgressBar/VideoProgressBar";
import usePriceCountdown from "~/components/videoProductPage/common/usePriceCountdown";
import { useAuth } from "~/hooks/auth/useAuth";
import { usePriceOfferInfoCheckout } from "~/hooks/usePriceOfferInfo";
import { getResponsiveImageURL } from "~/util/cloudinary-utils";
import {
  formatMoney,
  convertSecondsToFriendlyDuration,
  convertToRelativeDate,
  displayNumberWithCommas,
  shouldShowRelativeDate,
} from "~/util/stringUtils";
import { getPathForProduct, getProductWatchPath } from "~/util/stringUtils";

import * as styles from "./VideoCard.css";

export const VideoCard = ({
  key,
  video,
  isForLibrary = false,
  isForProductPage = false,
  hasLibraryTag = false,
  progress = 0,
}: {
  key: string;
  video: ProductData;
  isForLibrary: boolean;
  isForProductPage?: boolean;
  hasLibraryTag?: boolean | null;
  progress?: number;
}): React.ReactElement => {
  const { expired } = usePriceCountdown({
    targetDate: video.priceInfo.crossedPriceApplicableFrom || "",
  });

  const { shouldUserSeeOfferCalculation } = usePriceOfferInfoCheckout();
  const [showPriceOffer, setShowPriceOffer] = useState(false);
  const { user, loadingCurrentUser } = useAuth();
  const [showAsPurchased, setShowAsPurchased] = useState(hasLibraryTag);

  useEffect(() => {
    const shouldUserSeeOffer = shouldUserSeeOfferCalculation(video.priceInfo);

    setShowPriceOffer(shouldUserSeeOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadingCurrentUser]);

  useEffect(() => {
    setShowAsPurchased(!!user && hasLibraryTag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadingCurrentUser]);

  return (
    <div className={!isForProductPage ? styles.cardContainer : ""}>
      <Link
        href={
          isForLibrary || showAsPurchased ? getProductWatchPath(video) : getPathForProduct(video)
        }
        target={isForProductPage ? "_blank" : "_blank"}
      >
          <div className={isForProductPage ? styles.productPageContainer : ""}>
            <div
              className={
                isForProductPage
                  ? classNames(styles.cardImgContainer, styles.cardImgContainerSmall)
                  : classNames(styles.cardImgContainer)
              }
            >
              <div className={styles.imgWrapper}>
                <ImageWithBlur
                  src={getResponsiveImageURL(video.productImageURL, "600")}
                  width={isForProductPage ? 236 : 306}
                  height={isForProductPage ? 236 : 306}
                  layout={"responsive"}
                  objectFit={"cover"}
                  alt={video.title}
                />
              </div>
              <div className={styles.cardImgOverlay}>
                <PlayVideoIcon width={58} height={57} />
              </div>
              {(showAsPurchased || isForLibrary) && (
                <VideoProgressBar barHeight={7} progressPercentage={progress} />
              )}
            </div>
            <div className={classNames(styles.cardBody)}>
              <h1 className={isForProductPage ? styles.cardTitleSmall : styles.cardTitleLarge}>
                {video.title}
              </h1>

              {isForLibrary && (
                <CreatorInfo
                  avatar={video.creatorProfileImageURL}
                  name={video.creatorName}
                  slug={video.storefrontSlug}
                  style={{ marginBottom: 12 }}
                  isPurchasedVideo={isForLibrary}
                  totalVideoLengthInSeconds={video.totalVideoLengthInSeconds}
                />
              )}

              <div className={styles.cardDetails}>
                {!isForLibrary &&
                  video.ratingInfo &&
                  video.ratingInfo.ratingScore &&
                  video.ratingInfo.numRatings !== 0 && (
                    <>
                      <StarReviewsIcon width={17} height={16} />
                      <span className={styles.whiteSpace}>
                        {video.ratingInfo?.ratingScore} (
                        {displayNumberWithCommas(video.ratingInfo?.numRatings)})
                      </span>
                      <DotSeparatorIcon />
                    </>
                  )}

                {!isForLibrary && (
                  <span className={styles.whiteSpace}>
                    {convertSecondsToFriendlyDuration(video.totalVideoLengthInSeconds)}
                  </span>
                )}

                {!isForLibrary && !isForProductPage && shouldShowRelativeDate(video.createdAt) && (
                  <>
                    <DotSeparatorIcon />
                    <span className={styles.whiteSpace}>
                      {convertToRelativeDate(video.createdAt)}
                    </span>
                  </>
                )}
              </div>

              {!isForLibrary && !showAsPurchased ? (
                <h2 className={isForProductPage ? styles.salePriceSmall : styles.salePrice}>
                  {formatMoney(
                    !showPriceOffer ? video.priceInfo.crossedPrice : video.priceInfo.price,
                  )}{" "}
                  {showPriceOffer && (
                    <small className={styles.regularPrice}>
                      <del>{formatMoney(video.priceInfo.crossedPrice)}</del>
                    </small>
                  )}
                </h2>
              ) : (
                !isForLibrary &&
                showAsPurchased && <h2 className={styles.libraryTag}>In Library</h2>
              )}
            </div>
          </div>
      </Link>
    </div>
  );
};
